import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Barcode/QR Scanner Component
 * Uses device camera to scan barcodes and QR codes
 * Supports: EAN-13, UPC-A, Code128, Code39, QR codes
 */
const BarcodeScanner = ({ onScan, onClose, title = "Scan Barcode or QR Code" }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    startScanner();
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    try {
      setError(null);
      setIsScanning(true);

      // Initialize scanner
      html5QrCodeRef.current = new Html5Qrcode("barcode-scanner");

      // Start scanning with both camera and supported formats
      await html5QrCodeRef.current.start(
        { facingMode: "environment" }, // Use back camera
        {
          fps: 10, // Frames per second
          qrbox: { width: 250, height: 250 }, // Scanning box size
          formatsToSupport: [
            0, // QR_CODE
            8, // EAN_13
            11, // UPC_A
            14, // CODE_128
            13, // CODE_39
          ]
        },
        (decodedText, decodedResult) => {
          // Success callback
          console.log('Scanned:', decodedText);
          onScan(decodedText);
          stopScanner();
        },
        (errorMessage) => {
          // Error callback (scanning errors, not critical)
          // Don't show these as they're normal scanning feedback
        }
      );
    } catch (err) {
      console.error('Scanner error:', err);
      setError('Failed to start camera. Please check permissions and try again.');
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    try {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      }
      setIsScanning(false);
    } catch (err) {
      console.error('Error stopping scanner:', err);
    }
  };

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Scanner Area */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="relative">
            <div 
              id="barcode-scanner" 
              className="rounded-lg overflow-hidden border-2 border-blue-300"
              style={{ width: '100%', minHeight: '300px' }}
            />
            
            {isScanning && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 mb-2">
                  Position the barcode or QR code within the box
                </p>
                <div className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-blue-600">Scanning...</span>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-800 font-medium mb-2">Supported Formats:</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
              <div>• EAN-13</div>
              <div>• UPC-A</div>
              <div>• Code 128</div>
              <div>• Code 39</div>
              <div>• QR Codes</div>
            </div>
          </div>

          <Button 
            onClick={handleClose} 
            variant="outline" 
            className="w-full mt-4"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
